import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { merge, of } from 'rxjs';
import { catchError, filter, map, mergeMap, take, takeUntil } from 'rxjs/operators';
import {
  productModificationResultFailed,
  productModificationResultReceived,
} from 'state/productModificationForm/productModificationForm.action';
import * as actionTypes from 'state/productModificationForm/productModificationForm.actionTypes';
import type { EpicWithThunk } from 'state/state.common';
import type { WorkspaceTabRemoved } from 'state/workspaces/workspace.actions';
import { throwOnTimeout, wrapError } from 'utils/rxjs';
import { fatEventApiConnector } from 'utils/streaming/api';
import { productModificationResult } from 'utils/streaming/messages';

export type AstroProductModificationStatus = 'Accepted' | 'Failed' | 'Rejected' | 'Updated' | 'Timeout' | 'Cancelled';

export interface ProductModificationWorkflowError {
  originMessage?: string;
  rejectionCode?: string;
  rejectionMessage?: string;
  rejectionMessageCode?: string;
  rejectionOrigin?: string;
  rejectionType?: string;
  requestId?: string;
  outOfRangeCondition?: string;
  outOfRangeHighLimit?: number;
  outOfRangeLowLimit?: number;
}

export type Technical = Readonly<{
  login: string;
  requestId: string;
}>;

export type ProductModificationAck = Readonly<{
  status: AstroProductModificationStatus;
  technical: Technical;
  errorMessages?: ProductModificationWorkflowError[];
}>;

export type ProductModificationResult = Readonly<{
  productModificationAck: ProductModificationAck;
}>;

export function productModificationResultEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, _, { frontLogger, oldHttpClient, getSgmeConfiguration, streaming }) =>
    action$.pipe(
      ofType(actionTypes.PRODUCT_MODIFICATION_APPLY_REQUESTED),
      mergeMap(({ tabId, requestId }) => {
        const requestFailed$ = action$.pipe(
          ofType('PRODUCT_MODIFICATION_APPLY_REQUEST_FAILED'),
          filter(({ requestId: failedRequestId }) => failedRequestId === requestId),
        );

        const tabRemoved$ = action$.pipe(
          ofType('WORKSPACE_TAB_REMOVED'),
          filter((action: WorkspaceTabRemoved) => action.id === tabId),
        );
        const productModificationTimeoutMs = getSgmeConfiguration().productModificationTimeoutMs;

        return productModificationResult(streaming).pipe(
          filter(event => event.quoteId === requestId),
          mergeMap(lightEvent =>
            fatEventApiConnector(oldHttpClient)
              .getFatEventWithTabId<ProductModificationResult>(lightEvent, tabId)
              .pipe(
                map(event => productModificationResultReceived(tabId, event)),
                wrapError(
                  error => new apiErrorHelper.EpicApiError(apiErrorHelper.defaultTechnicalErrorKey, error),
                  appScheduler,
                ),
              ),
          ),
          throwOnTimeout(
            productModificationTimeoutMs,
            new apiErrorHelper.TimeoutError(productModificationTimeoutMs, 'productModification.timeoutWarning'),
            appScheduler,
          ),
          takeUntil(merge(tabRemoved$, requestFailed$)),
          take(1),
          catchError((error: Error) => {
            frontLogger.error('An error occurred in productModificationResultEpic {@FrontError}', error);
            return of(productModificationResultFailed(tabId, apiErrorHelper.toApiErrorMessage(error), error));
          }),
        );
      }),
    );
}
